import React, { useCallback, useState } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { SetLoginForm, Translation } from '@app/models';
import { loginForms } from '@app/enums';
import { Typography } from '@material-ui/core';
import Icon from '@app/components/Icon/index';
import { PasslyButton } from '@app/components';
import { accountService } from '@app/services';
import { AxiosError, AxiosResponse } from 'axios';
import { Reset2faTokenChooseComponent, Reset2faTokenRow } from '@app/pages/login/components';
import { genericErrorToast } from '@app/helpers';
import { returnToLogin } from '@app/pages/login/helpers';
import { signInErrorClean } from '@app/store/actions';
import { useDispatch } from 'react-redux';

interface IProps {
    setLoginForm: SetLoginForm;
    providers: string[];
    verifyUser?: boolean;
    contextToken?: string;
}

function getComponent(provider: string, setLoginForm: SetLoginForm, translation: Translation, verifyUser: boolean) {
    switch (provider) {
        case 'otp':
            return {
                onClick: () => {
                    setLoginForm(loginForms.twoFactorOtp);
                },
                Icon: <Icon icon='oneTimePasswordIco' className='icon__OTP' />,
                title: translation('MFA_OTP_PROVIDER'),
                desc: verifyUser ? translation('MFA_OTP_PROVIDER_DESC_VERIFY_USER') : translation('MFA_OTP_PROVIDER_DESC')
            };
        case 'push':
            return {
                onClick: () => {
                    setLoginForm(loginForms.twoFactorPush);
                },
                Icon: <Icon icon='pushAuthIco' className='icon__push-auth' />,
                title: translation('MFA_PUSH_PROVIDER'),
                desc: verifyUser ? translation('MFA_PUSH_PROVIDER_DESC_VERIFY_USER') : translation('MFA_PUSH_PROVIDER_DESC')
            };
        case 'u2f':
            return {
                onClick: () => {
                    setLoginForm(loginForms.twoFactorU2F);
                },
                Icon: <Icon icon='utfTokenIco' className='icon__yubikey-u2f--micro' />,
                title: translation('MFA_U2F_PROVIDER'),
                desc: translation('MFA_U2F_PROVIDER_DESC')
            };

        default:
            return null;
    }
}

export const TwoFactorList: React.SFC<IProps> = ({ setLoginForm, providers, verifyUser = false, contextToken }) => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();
    const [resetViaPhone, setResetViaPhone] = useState(false);
    const [resetAnotherWayClicked, setResetAnotherWayClicked] = useState(false);
    const [is2FATokenResetForm, setIs2FATokenResetForm] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleError = (error: AxiosError<any>) => {
        setIsLoading(false);
        genericErrorToast(error);
    };

    const goToReset2FAtoken = useCallback(() => {
        setIs2FATokenResetForm(true);
    }, []);

    const handleCantAccessAuthenticatorApp = () => {
        setIsLoading(true);
        accountService.check2FAResetMetadata({ contexttoken: contextToken }, handleError, (res: AxiosResponse) => {
            dispatch(signInErrorClean());
            setIsLoading(false);
            setResetAnotherWayClicked(true);
            setResetViaPhone(res.data.resetViaPhone);
        });
    };

    return (
        <div className='loginList'>
            {is2FATokenResetForm && contextToken ? (
                <Reset2faTokenChooseComponent contextToken={contextToken} setLoginForm={setLoginForm} />
            ) : resetAnotherWayClicked ? (
                <>
                    <h1>{translation('RESET_OPTIONS')}</h1>
                    {resetViaPhone ? (
                        <Reset2faTokenRow onClick={goToReset2FAtoken} />
                    ) : (
                        <div className='loginList__cant-reset'>
                            {translation('LOGIN_CANT_RESET_2FA')}
                            <div className='loginList__buttons'>
                                <PasslyButton
                                    color='primary'
                                    variant='contained'
                                    className='loginList__button-success'
                                    onClick={returnToLogin(setLoginForm)}
                                >
                                    {translation('RESET_BACK')}
                                </PasslyButton>
                            </div>
                        </div>
                    )}
                </>
            ) : providers.length === 0 ? (
                <div className='loginList__centered'>
                    <Typography variant='h3'>{translation('MFA_VERIFICATION_SIGNIN')}</Typography>

                    <div className='loginList__instructions'>
                        <Typography variant='body1'>{translation('NO_USER_MFA_FOR_APP')}</Typography>
                    </div>
                </div>
            ) : (
                <>
                    <h1>{translation('SIGNIN_VERIFICATION')}</h1>
                    <p className='loginList__form-text'>{translation('SIGNIN_VERIFICATION_OPTIONS_TITLE')}</p>
                    <ul className='loginList__wrapper'>
                        {providers.map((provider, index) => {
                            const componentInfo = getComponent(provider, setLoginForm, translation, verifyUser);
                            if (!componentInfo) return null;

                            const { onClick, Icon, title, desc } = componentInfo;
                            return (
                                <li className='twoFactorList' key={index} onClick={onClick}>
                                    <div className='twoFactorList__icon'>{Icon}</div>
                                    <div className='twoFactorList__info'>
                                        <div className='twoFactorList__info-title'>{title}</div>
                                        <div className='twoFactorList__info-userName'>{desc}</div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    {contextToken ? (
                        <PasslyButton
                            className='twofactorauth__cant-access'
                            id='cantAccess'
                            color='primary'
                            onClick={handleCantAccessAuthenticatorApp}
                            disableRipple={true}
                            isLoading={isLoading}
                        >
                            {translation('LOGIN_CANT_ACCESS_AUTH_APP')}
                        </PasslyButton>
                    ) : null}
                </>
            )}
        </div>
    );
};
